/**
 * Category selector for the inventory list — an @ui `Tabs` bar (one tab per
 * hardware category) with a live count `Badge`, styled to match the Customers
 * list tabs. The active category is carried in the URL by the parent, so a
 * filtered inventory view is shareable.
 */
import { useMemo } from 'react';
import { Tabs, TabsList, TabsTrigger, Badge } from '@vnetwork/ui/primitives';
import { useDashboardStats } from '@/api/capacities';
import {
  CATEGORY_TABS,
  buildCategoryCountMap,
  categoryCount,
} from './list-configs/category-cards-config';

export interface CategoryTabsProps {
  /** Currently selected tab value (e.g. "server"). */
  value: string;
  onChange: (value: string) => void;
}

export function CategoryTabs({ value, onChange }: CategoryTabsProps) {
  // Counts are supplementary — a failed/slow stats call must never block the list.
  const { data: stats } = useDashboardStats();
  const countMap = useMemo(() => buildCategoryCountMap(stats), [stats]);

  return (
    <Tabs value={value} onValueChange={onChange}>
      <TabsList style={{ marginBottom: 0, flexWrap: 'wrap' }}>
        {CATEGORY_TABS.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
            <Badge
              tone={value === tab.value ? 'primary' : 'neutral'}
              style={{ marginLeft: 'var(--vn-space-2)', fontVariantNumeric: 'tabular-nums' }}
            >
              {stats ? categoryCount(tab.value, countMap) : '—'}
            </Badge>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
